import { Link } from "@tanstack/react-router";
import { siteConfig } from "@/config/site";

import { useState } from "react";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { ButtonLink } from "@/components/ui-ext/Button";
import { CaseStudyTOC } from "@/components/caseStudy/CaseStudyTOC";
import { BrandFigure, figuresForSlug } from "@/components/media/BrandFigure";
import { Breadcrumbs } from "@/components/shared/Breadcrumbs";
import {
  getBlogPost,

  listBlogPosts,
  type BlogImage as BlogImageT,
  type BlogCategory,
  type BlogPost,
} from "@/content/blog";
import { SERVICES } from "@/content/services";
import { TOOLS } from "@/content/tools";
import { LOCATIONS } from "@/content/locations";
import { listAustinNeighborhoods } from "@/content/neighborhoods";

// Blog category → which Texas locations + Austin neighborhoods to surface.
// Local SEO / Case Teardown → deep local coverage; other categories → HQ + a few metros.
const CATEGORY_TO_LOCATION_SLUGS: Record<BlogCategory, string[]> = {
  "Local SEO": ["austin", "round-rock", "cedar-park", "san-antonio", "dallas", "houston"],
  "Case Teardown": ["austin", "round-rock", "cedar-park", "pflugerville", "san-antonio"],
  "AI Search": ["austin", "san-antonio", "dallas"],
  "Technical SEO": ["austin", "dallas", "houston"],
  "Content Strategy": ["austin", "san-antonio", "dallas"],
  Analytics: ["austin", "dallas", "houston"],
};

const CATEGORY_SHOWS_NEIGHBORHOODS: Record<BlogCategory, boolean> = {
  "Local SEO": true,
  "Case Teardown": true,
  "AI Search": false,
  "Technical SEO": false,
  "Content Strategy": false,
  Analytics: false,
};

const CATEGORY_TO_SERVICES: Record<BlogCategory, string[]> = {
  "AI Search": ["aeo-geo", "content-strategy", "technical-seo"],
  "Content Strategy": ["content-strategy", "aeo-geo", "link-building"],
  "Technical SEO": ["technical-seo", "analytics-reporting", "web-design"],
  "Local SEO": ["local-seo", "google-business-profile", "analytics-reporting"],
  "Case Teardown": ["local-seo", "technical-seo", "analytics-reporting"],
  Analytics: ["analytics-reporting", "cro", "technical-seo"],
};

const CATEGORY_TO_TOOLS: Record<BlogCategory, string[]> = {
  "AI Search": ["geo-score", "schema-explorer", "og-preview"],
  "Content Strategy": ["reading-grade", "heading-outliner", "keyword-density"],
  "Technical SEO": ["robots-tester", "schema-explorer", "hreflang-generator"],
  "Local SEO": ["local-business-schema", "serp-preview", "meta-length-checker"],
  "Case Teardown": ["serp-preview", "og-preview", "utm-builder"],
  Analytics: ["utm-builder", "slug-generator", "meta-length-checker"],
};

function buildSrcSet(src: string): string | undefined {
  if (!src.includes("images.unsplash.com")) return undefined;
  const widths = [480, 768, 1024, 1280, 1600, 1920];
  return widths.map((w) => `${src.replace(/([?&])w=\d+/, `$1w=${w}`)} ${w}w`).join(", ");
}

function ResponsiveBlogImage({
  image,
  eager = false,
  sizes,
  aspect = "16/9",
}: {
  image: BlogImageT;
  eager?: boolean;
  sizes: string;
  aspect?: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const srcSet = buildSrcSet(image.src);
  return (
    <figure className="-mx-4 sm:mx-0">
      <div
        className="relative overflow-hidden rounded-2xl border border-border bg-card/40"
        style={{ aspectRatio: aspect }}
      >
        <div
          aria-hidden="true"
          className={`absolute inset-0 bg-gradient-to-br from-surface-2 via-card to-surface-2 transition-opacity duration-500 ${loaded ? "opacity-0" : "opacity-100 animate-pulse"}`}
        />
        <img
          src={image.src}
          srcSet={srcSet}
          sizes={sizes}
          alt={image.alt}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={eager ? "high" : "auto"}
          width={1600}
          height={900}
          onLoad={() => setLoaded(true)}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ease-out ${loaded ? "opacity-100" : "opacity-0"}`}
        />
      </div>
      {(image.caption || image.credit) && (
        <figcaption className="mt-3 px-1 text-xs text-muted-foreground flex flex-col sm:flex-row sm:justify-between gap-1">
          {image.caption && <span className="italic">{image.caption}</span>}
          {image.credit && <span className="text-muted-foreground/70">{image.credit}</span>}
        </figcaption>
      )}
    </figure>
  );
}

export function BlogPostView({ post }: { post: BlogPost }) {
  const toc = post.sections.map((s, i) => ({
    id: `s-${i}`,
    label: `${String(i + 1).padStart(2, "0")} · ${s.h}`,
  }));

  const related = (post.related ?? [])
    .map(getBlogPost)
    .filter(Boolean) as NonNullable<ReturnType<typeof getBlogPost>>[];

  const remaining = listBlogPosts().filter((p) => p.slug !== post.slug).slice(0, 3);

  const relatedServiceSlugs = CATEGORY_TO_SERVICES[post.category] ?? [];
  const relatedServices = relatedServiceSlugs
    .map((s) => SERVICES.find((x) => x.slug === s))
    .filter(Boolean) as (typeof SERVICES)[number][];

  const relatedToolSlugs = CATEGORY_TO_TOOLS[post.category] ?? [];
  const relatedTools = relatedToolSlugs
    .map((s) => TOOLS.find((t) => t.slug === s))
    .filter(Boolean) as typeof TOOLS;

  const relatedLocationSlugs = CATEGORY_TO_LOCATION_SLUGS[post.category] ?? ["austin"];
  const relatedLocationsForPost = relatedLocationSlugs
    .map((slug) => LOCATIONS.find((l) => l.slug === slug))
    .filter(Boolean) as (typeof LOCATIONS)[number][];
  const showNeighborhoods = CATEGORY_SHOWS_NEIGHBORHOODS[post.category] ?? false;
  const austinNeighborhoods = showNeighborhoods ? listAustinNeighborhoods() : [];

  const shareUrl = `${siteConfig.origin}/${post.slug}/`;
  const shareTitle = `${post.title} — ${siteConfig.name}`;


  const shareLinks = [
    { label: "Share on X", href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}` },
    { label: "Share on LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
    { label: "Share on Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
    { label: "Share via Email", href: `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareUrl)}` },
  ];

  const faqs = post.sections
    .filter(s => s.h.toLowerCase().includes('faq') || s.h.includes('?'))
    .map(s => ({ q: s.h, a: s.p.join(' ') }));

  return (
    <div>
      {/* JSON-LD scripts are injected via Route.head in blog.index.tsx or the dynamic route handler */}
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <div className="container-page pt-8">
          <Breadcrumbs />
        </div>



        <div className="container-page pt-6">
          <div className="max-w-4xl rounded-2xl border border-border/70 bg-card/40 p-4 md:p-5 text-sm text-foreground/85 leading-relaxed">
            <span className="font-medium text-foreground">Field-noted from ${siteConfig.contact.address.city}, ${siteConfig.contact.address.state}</span>{" "}
            — written by ${siteConfig.founder.name}, founder of ${siteConfig.name}, based on live SEO and AI-search
            engagements with ${siteConfig.contact.address.city} and ${siteConfig.contact.address.state} operators.

          </div>
        </div>

        <section className="container-page pt-8 pb-10 max-w-4xl">
          <EyebrowLabel>{post.category}</EyebrowLabel>
          <h1 className="mt-4 font-display text-4xl md:text-6xl leading-[1.02] tracking-tight">{post.title}</h1>
          <p className="mt-6 text-xl text-muted-foreground leading-relaxed">{post.dek}</p>
          <div className="mt-8 flex flex-wrap items-center gap-4 text-xs uppercase tracking-widest text-muted-foreground border-t border-border pt-6">
            <Link to="/authors/$slug/" params={{ slug: post.authorSlug }} className="text-foreground hover:text-primary transition">
              {siteConfig.founder.name}
            </Link>

            <span>·</span>
            <span>{new Date(post.publishedISO).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
            <span>·</span>
            <span>{post.readingMinutes} min read</span>
            {post.updatedISO && (<><span>·</span><span>Updated {new Date(post.updatedISO).toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span></>)}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-2" aria-label="Share this article">
            <span className="text-[0.65rem] uppercase tracking-widest text-muted-foreground mr-1">Share</span>
            {shareLinks.map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                className="inline-flex items-center rounded-full border border-border px-3 py-1.5 text-xs text-foreground/85 hover:border-primary hover:text-primary transition">
                {s.label.replace("Share on ", "").replace("Share via ", "")}
              </a>
            ))}
          </div>
        </section>

        <section className="container-page pb-24 grid gap-12 lg:grid-cols-[240px_minmax(0,1fr)]">
          <aside className="hidden lg:block">
            <div className="sticky top-24"><CaseStudyTOC items={toc} /></div>
          </aside>

          <article className="max-w-3xl">
            <div className="mb-10">
              <ResponsiveBlogImage image={post.heroImage} eager sizes="(min-width: 1024px) 720px, (min-width: 640px) calc(100vw - 4rem), 100vw" />
            </div>

            <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6 md:p-8 mb-12">
              <div className="text-xs uppercase tracking-widest text-primary">Key takeaways</div>
              <ul className="mt-3 space-y-2 text-sm text-foreground/90">
                {post.keyTakeaways.map((k) => (
                  <li key={k} className="flex gap-2"><span className="text-primary">·</span>{k}</li>
                ))}
              </ul>
            </div>

            {post.intro.map((p, i) => (
              <p key={i} className="text-lg leading-relaxed text-foreground/90 mb-5">{p}</p>
            ))}

            <BrandFigure
              className="mt-10"
              kind={figuresForSlug(post.slug, 2)[0]}
              title={`${post.title} — the shape of the problem`}
            />

            {post.sections.map((s, i) => {
              const midpoint = Math.floor(post.sections.length / 2);
              const lateMark = Math.max(midpoint + 1, post.sections.length - 2);
              return (
                <div key={i}>
                  <section id={`s-${i}`} className="mt-14 scroll-mt-24">
                    <div className="text-xs uppercase tracking-widest text-primary">{String(i + 1).padStart(2, "0")}</div>
                    <h2 className="mt-2 font-display text-3xl md:text-4xl tracking-tight leading-tight">{s.h}</h2>
                    {s.p.map((para, j) => (
                      <p key={j} className="mt-5 text-[1.05rem] leading-relaxed text-foreground/85">{para}</p>
                    ))}
                  </section>
                  {i === midpoint - 1 && (
                    <div className="mt-14">
                      <ResponsiveBlogImage image={post.bodyImage} sizes="(min-width: 1024px) 720px, (min-width: 640px) calc(100vw - 4rem), 100vw" />
                    </div>
                  )}
                  {i === lateMark && (
                    <BrandFigure
                      className="mt-14"
                      kind={figuresForSlug(post.slug, 2)[1]}
                      title={`How this compounds once it's implemented`}
                    />
                  )}
                </div>
              );
            })}

            <div className="mt-16 pt-8 border-t border-border flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <span key={t} className="text-xs px-3 py-1 rounded-full border border-border text-muted-foreground">#{t}</span>
              ))}
            </div>

            <div className="mt-14 rounded-3xl border border-border p-8 md:p-10 bg-gradient-to-br from-primary/10 to-transparent">
              <EyebrowLabel>Working on this?</EyebrowLabel>
              <h3 className="mt-3 font-display text-2xl md:text-3xl tracking-tight">We ship this exact playbook in engagements.</h3>
              <p className="mt-3 text-muted-foreground leading-relaxed">
                If what you just read maps to a problem in front of you, a 30-minute conversation is the fastest way to know if we can help.
              </p>
              <div className="mt-6">
                <Link to="/contact/" search={{ location: "", service: "" }} className="inline-flex h-10 items-center justify-center rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition-transform hover:-translate-y-0.5">
                  Start a project <span aria-hidden>→</span>
                </Link>
              </div>
            </div>
          </article>
        </section>

        {relatedServices.length > 0 && (
          <section className="container-page pb-16" aria-labelledby="related-svcs">
            <div className="rounded-3xl border border-border p-8 md:p-10 bg-card/30">
              <EyebrowLabel>Related services</EyebrowLabel>
              <h2 id="related-svcs" className="mt-3 font-display text-2xl md:text-3xl tracking-tight">Programmes that pair with this article.</h2>
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                {relatedServices.map((s) => (
                  <Link key={s.slug} to="/services/$slug/" params={{ slug: s.slug }}
                    className="rounded-2xl border border-border p-5 bg-background hover:border-primary/40 transition-all">

                    <div className="text-[0.65rem] uppercase tracking-widest text-primary">{s.category}</div>
                    <div className="mt-1 font-display text-lg">{s.title}</div>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{s.metaDescription}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {relatedTools.length > 0 && (
          <section className="container-page pb-16" aria-labelledby="related-tools">
            <div className="rounded-3xl border border-border p-8 md:p-10 bg-card/30">
              <EyebrowLabel>Free SEO tools</EyebrowLabel>
              <h2 id="related-tools" className="mt-3 font-display text-2xl md:text-3xl tracking-tight">Put this article into practice.</h2>
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                {relatedTools.map((t) => (
                  <Link key={t.slug} to="/tools/$slug/" params={{ slug: t.slug }}
                    className="rounded-2xl border border-border p-5 bg-background hover:border-primary/40 transition-all">

                    <div className="text-[0.65rem] uppercase tracking-widest text-primary">Free tool</div>
                    <div className="mt-1 font-display text-lg">{t.name}</div>
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{t.tagline}</p>
                  </Link>
                ))}
              </div>
              <div className="mt-6">
                <Link to="/tools/" className="text-sm text-primary story-link">Browse all free SEO tools →</Link>
              </div>

            </div>
          </section>
        )}

        {relatedLocationsForPost.length > 0 && (
          <section className="container-page pb-16" aria-labelledby="local-coverage">
            <div className="rounded-3xl border border-border p-8 md:p-10 bg-card/30">
              <EyebrowLabel>Local coverage</EyebrowLabel>
              <h2 id="local-coverage" className="mt-3 font-display text-2xl md:text-3xl tracking-tight">
                Where we run this playbook across ${siteConfig.contact.address.state}.
              </h2>

              <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
                The tactics above are how we ship {post.category.toLowerCase()} work.
                These are the ${siteConfig.contact.address.state} markets we ship them in — every page is a
                dedicated local playbook with its own competitor landscape and
                FAQs.
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {relatedLocationsForPost.map((l) => (
                  <Link
                    key={l.slug}
                    to={"/seo-agency-{$slug}" as any}
                    params={{ slug: l.slug } as any}
                    className="rounded-2xl border border-border p-4 bg-background hover:border-primary/40 transition-all"
                  >

                    <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                      {l.region}
                    </div>
                    <div className="mt-1 font-display text-base">
                      {l.city}, {siteConfig.contact.address.state}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
                      {l.hero.deck}
                    </div>
                  </Link>
                ))}
              </div>

              {austinNeighborhoods.length > 0 && (
                <div className="mt-8">
                  <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                    {siteConfig.contact.address.city} neighborhood playbooks
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {austinNeighborhoods.map((n) => (
                      <Link
                        key={n.slug}
                        to={"/seo-agency-austin/" as any}
                        className="rounded-xl border border-border/70 px-4 py-3 text-sm hover:border-primary/40 transition-all flex items-center justify-between gap-3"
                      >
                        <span className="font-medium">{n.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {n.displaySubtitle}
                        </span>
                      </Link>
                    ))}
                  </div>
                  <div className="mt-4 text-xs text-muted-foreground">
                    Or jump to the{" "}
                    <Link
                      to={"/seo-agency-{$slug}" as any}
                      params={{ slug: "austin" } as any}
                      className="text-primary story-link"
                    >
                      Austin neighborhoods overview
                    </Link>

                    .
                  </div>
                </div>
              )}
            </div>
          </section>
        )}



        <section className="container-page pb-16" aria-labelledby="post-news">
          <div className="rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent p-8 md:p-10">
            <EyebrowLabel>Growth briefing</EyebrowLabel>
            <h2 id="post-news" className="mt-3 font-display text-2xl md:text-3xl tracking-tight">One field-noted email a month. Nothing else.</h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">
              Local SEO wins, AI-search shifts, and case teardowns like this one — written from live engagements with Austin and Central Texas operators. Double opt-in. Unsubscribe anytime.
            </p>
            <p className="mt-4 text-sm text-foreground/80">
              Scroll to the footer to subscribe, or{" "}
              <Link to="/contact/" search={(prev) => ({ ...prev, location: "", service: "" })} className="text-primary story-link">start a project with our Austin team →</Link>
            </p>
          </div>
        </section>

        <section className="container-page pb-32">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-6">Keep reading</div>
          <div className="grid gap-6 md:grid-cols-3">
            {(related.length ? related : remaining).slice(0, 3).map((r) => (
              <a key={r.slug} href={`/${r.slug}/`}
                className="group rounded-2xl border border-border p-6 bg-card/30 hover:bg-card/50 hover:border-primary/40 transition-all">
                <EyebrowLabel>{r.category}</EyebrowLabel>
                <h4 className="mt-3 font-display text-xl leading-tight tracking-tight">{r.title}</h4>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{r.dek}</p>
              </a>
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
